import {
  clearStoredSession,
  getStoredSession,
  setStoredSession,
} from "@fogo/sessions-sdk-web";
import {
  establishSession,
  reestablishSession,
  type SessionContext,
  createSessionConnection,
  SessionResultType,
  type Session,
  Network,
  createSessionContext,
} from "@fogo/sessions-sdk";
import { PublicKey, VersionedTransaction } from "@solana/web3.js";
import type { CommonResponse } from "@/types/response";
import store from "@/store";
import { reactive, ref } from "vue";
import { tryCatch } from "@/types/utils";

export type FogoSolanaConnectionOptions = Parameters<
  typeof createSessionConnection
>[0];
export type FogoEstablishSessionOptions = Parameters<
  typeof establishSession
>[0];
export type FogoSessionConnection = ReturnType<typeof createSessionConnection>;

export type FogoSessionOptions = FogoSolanaConnectionOptions & {
  domain?: string;
  limits?: Map<PublicKey, bigint>;
  unlimited?: boolean;
  expires: Date;
  duration: number;
};

export type FluxFogoSessionWallet = {
  publicKey: PublicKey | null;
  signMessage: ((message: Uint8Array) => Promise<Uint8Array>) | undefined;
};

export type FluxFogoSessionParams = {
  wallet: FluxFogoSessionWallet;
  options?: Partial<FogoSessionOptions>;
};

export const ONE_SECOND_IN_MS = 1000;
export const ONE_MINUTE_IN_MS = 60 * ONE_SECOND_IN_MS;
export const ONE_HOUR_IN_MS = 60 * ONE_MINUTE_IN_MS;
export const ONE_DAY_IN_MS = 24 * ONE_HOUR_IN_MS;
export const ONE_WEEK_IN_MS = 7 * ONE_DAY_IN_MS;
export const DEFAULT_SESSION_DURATION = ONE_WEEK_IN_MS;

export function useFogoSession() {
  // data
  const options = reactive<FogoSessionOptions>({
    network: Network.Testnet,
    expires: getExpiresDuration(DEFAULT_SESSION_DURATION),
    duration: DEFAULT_SESSION_DURATION,
    domain: "https://fogo.fun",
  });
  const wallet = ref<FluxFogoSessionWallet>();
  const connection = ref<FogoSessionConnection>();
  const context = ref<SessionContext>();
  const session = ref<Session | undefined>();
  const expirationTimeout = ref<NodeJS.Timeout>();

  // methods
  async function connect({
    wallet: w,
    options: opt,
  }: FluxFogoSessionParams): Promise<
    CommonResponse<{
      context: SessionContext;
      session: Session;
    }>
  > {
    return tryCatch<{ context: SessionContext; session: Session }>(async () => {
      wallet.value = w;
      Object.assign(options, opt);

      // create session context
      const { context } = await createSession(options);

      // create session
      const { options: fogoEstablishSessionOptions } =
        getFogoEstablishSessionOptions({
          context,
          params: { wallet: w, options: opt },
        });

      const newSession = await establishSession(fogoEstablishSessionOptions);

      if (newSession.type === SessionResultType.Failed) {
        return { error: new Error(newSession.error.toString()), ok: false };
      }

      // store session
      await setStoredSession(opt?.network!, {
        sessionKey: newSession.session.sessionKey,
        walletPublicKey: newSession.session.walletPublicKey,
      });

      session.value = newSession.session;
      checkExpirationRoutine();

      return {
        data: {
          context,
          session: newSession.session,
        },
        ok: true,
      };
    });
  }

  async function disconnect(): Promise<void> {
    clearInterval(expirationTimeout.value!);
    expirationTimeout.value = undefined;
    
    if (session.value?.walletPublicKey) {
      clearStoredSession(options.network, session.value.walletPublicKey);
    }
    session.value = undefined;
    context.value = undefined;
    connection.value = undefined;
  }

  async function createSession(
    opt?: Partial<FogoSessionOptions>,
  ): Promise<{ connection: FogoSessionConnection; context: SessionContext }> {
    connection.value = createSessionConnection({
      network: opt?.network!,
    });

    context.value = await createSessionContext({
      connection: connection.value,
      domain: opt?.domain,
    });

    return { connection: connection.value, context: context.value };
  }

  async function getOrEstablishSession({
    wallet: w,
    options: opt,
  }: FluxFogoSessionParams): Promise<
    CommonResponse<{
      context: SessionContext;
      session: Session;
    }>
  > {
    return tryCatch<{ context: SessionContext; session: Session }>(async () => {
      if (!w.publicKey) {
        return { error: new Error("Wallet not connected"), ok: false };
      }

      wallet.value = w;
      Object.assign(options, opt);

      const storedSession = await getStoredSession(
        options?.network!,
        w.publicKey,
      );

      // create a new session
      if (!storedSession) {
        return connect({ wallet: w, options: opt });
      }

      // restore old session
      const { context } = await createSession(options);

      const newSession = await reestablishSession(
        context,
        storedSession.walletPublicKey,
        storedSession.sessionKey,
      );

      if (!newSession) {
        disconnect();
        return connect({ wallet: w, options: opt });
        // return { error: new Error('Failed to restore session'), ok: false };
      }

      await setStoredSession(options?.network!, {
        sessionKey: newSession.sessionKey,
        walletPublicKey: newSession.walletPublicKey,
      });

      session.value = newSession;
      checkExpirationRoutine();

      return { data: { context, session: newSession }, ok: true };
    });
  }

  async function sendTransaction({
    transaction,
  }: {
    transaction: VersionedTransaction;
  }): Promise<CommonResponse<string>> {
    return tryCatch<string>(async () => {
      if (isExpired()) {
        disconnect();
        return { error: new Error("Session expired"), ok: false };
      }
      if (!context.value) {
        return { error: new Error("Context not initialized"), ok: false };
      }
      if (!session.value) {
        return { error: new Error("Session not initialized"), ok: false };
      }

      const { signature } = await session.value.sendTransaction(transaction);

      return { data: signature, ok: true };
    });
  }

  // get session options
  function getFogoEstablishSessionOptions({
    context: sessionContext,
    params: { wallet, options },
  }: {
    context: SessionContext;
    params: FluxFogoSessionParams;
  }): {
    options: FogoEstablishSessionOptions;
  } {
    if (!wallet?.publicKey) {
      throw new Error("Fogo wallet public key not defined");
    }
    if (!wallet?.signMessage) {
      throw new Error("Fogo wallet signMessage not defined");
    }

    const opt: FogoEstablishSessionOptions = {
      context: sessionContext,
      walletPublicKey: wallet.publicKey!,
      expires: options?.expires!,
      ...(options?.limits === undefined || options?.limits.size === 0
        ? { unlimited: true }
        : { limits: options.limits }),

      signMessage: async (message: Uint8Array) => {
        if (wallet === undefined || wallet.signMessage === undefined) {
          throw new Error("Wallet not connected");
        }

        return {
          signedMessage: message,
          signature: await wallet.signMessage(message),
        };
      },
    };

    return { options: opt };
  }

  function isExpired(): boolean {
    if (!session.value) {
      return true;
    }
    if (!options.expires) {
      return true;
    }
    return new Date() >= options.expires;
  }

  function checkExpirationRoutine() {
    clearInterval(expirationTimeout.value!);
    expirationTimeout.value = setInterval(() => {
      if (isExpired()) {
        disconnect();
        store.commit("user/set_needs_fogo_reconnect", true);
      }
    }, ONE_MINUTE_IN_MS);
  }

  async function triggerReconnect(): Promise<void> {
    await disconnect();
    store.commit("user/set_needs_fogo_reconnect", true);
  }

  return {
    options,
    session,
    context,
    connection,

    getOrEstablishSession,
    disconnect,
    sendTransaction,
    isExpired,
    triggerReconnect,
  };
}

export function getExpiresDuration(expires: number): Date {
  return new Date(Date.now() + expires);
}

export const fogoSession = useFogoSession();
